import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FlexyForm, FormObject } from '@europrocurement/flexy-form';
import { Box } from '@mui/material';
import { SubmitHandler, useForm } from 'react-hook-form';
import { GenericModalMailType } from '@europrocurement/flexy-components/types/GenericModalMailType';
import { useModal, useUploader } from '@europrocurement/flexy-components';
import { MailApiSendMailMailCollectionRequest } from '@europrocurement/l2d-domain/openApi/ApiMail';
import { useMailService } from '@europrocurement/l2d-domain';
import { useSnackbar } from 'notistack';
import { type ContactItem } from '@europrocurement/l2d-modules/modules/Dossiers';
import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import { markDownToHtml } from '@europrocurement/l2d-utils';
import { faWandMagicSparkles } from '@fortawesome/pro-regular-svg-icons';
import { IaButton } from '@europrocurement/l2d-modules/modules/IaServices';
import useFormStructureGenericModal from '../../hooks/useFormStructureGenericModal';

/**
 * GenericModalMailContent component
 *
 * A flexible email composition component that provides a form for composing and sending emails.
 * It includes support for:
 * - Dynamic field visibility (recipients, CC, BCC, subject, body, attachments)
 * - Built-in AI-powered text correction and improvement features
 * - Custom field ordering
 * - Form context access for parent components
 * - Markdown to HTML conversion for email body
 * - File attachments support
 *
 * @component
 * @example
 * // Basic usage with just recipients and body
 * <GenericModalMailContent
 *   to={[{ emailAddress: 'user@example.com', firstName: 'John', lastName: 'Doe' }]}
 *   body="Email content here"
 *   showBody={true}
 *   onSuccess={() => console.log('Email sent')}
 * />
 *
 * @example
 * // Advanced usage with all features
 * <GenericModalMailContent
 *   to={recipients}
 *   cc={ccList}
 *   cci={bccList}
 *   subject="Email Subject"
 *   body="Email body content"
 *   showTo={true}
 *   showCC={true}
 *   showCCI={true}
 *   showSubject={true}
 *   showBody={true}
 *   showAttachments={true}
 *   showIaButtons={true}
 *   iaChildren={<CustomIaButton />}
 *   searchParamsTiersIds={[1, 2, 3]}
 *   getFormContext={(ctx) => setFormContext(ctx)}
 *   fieldsOrdering={['recipients', 'subject', 'iaButtons', 'mailBody']}
 *   autoBalisesForMail={true}
 *   onSuccess={handleSuccess}
 * />
 */
const GenericModalMailContent: React.FunctionComponent<GenericModalMailType> = function ({
    to,
    cc,
    cci,
    subject,
    body,
    attachments,
    searchParamsTiersIds,
    showTo = false,
    showCC = false,
    showCCI = false,
    showSubject = false,
    showBody = false,
    showAttachments = false,
    additionnalReactNode,
    showIaButtons = true,
    iaChildren,
    SxProps,
    customButtonsFooter,
    fieldsOrdering,
    getFormContext,
    autoBalisesForMail = false,
    onSuccess,
}) {
    const { files } = useUploader();
    const { enqueueSnackbar } = useSnackbar();
    const { mailApi } = useMailService();
    const { modalActions } = useModal();
    const keycloak = UseKeycloakService();
    const [submittingForm, setSubmittingForm] = useState(false);
    const senderEmail = useRef('');
    const senderName = useRef('');

    const formContext = useForm({
        mode: 'onTouched',
        defaultValues: {
            mailBody: body || '',
            subject: subject || '',
            recipients: to || [],
        } as FormObject,
    });

    // Build default IA Buttons with optional additional children
    const defaultIaButtons = useMemo(() => {
        if (!formContext || !showIaButtons || !showBody) {
            return null;
        }

        return (
            <>
                <IaButton
                    getParams={() => ({
                        textContent: formContext.getValues('mailBody'),
                    })}
                    onSuccess={(data) => {
                        const correctedText = data.answer;
                        if (correctedText) {
                            formContext.setValue('mailBody', correctedText);
                            enqueueSnackbar('Correction effectuée');
                        }
                    }}
                />
                <IaButton
                    applicationName="L2D_Resume"
                    label="Améliorer le texte"
                    icon={faWandMagicSparkles}
                    getParams={() => ({
                        textContent: formContext.getValues('mailBody'),
                    })}
                    onSuccess={(data) => {
                        const correctedText = data.answer;
                        if (correctedText) {
                            formContext.setValue('mailBody', correctedText);
                            enqueueSnackbar('Amélioration effectuée');
                        }
                    }}
                />
                {iaChildren}
            </>
        );
    }, [showIaButtons, showBody, iaChildren, formContext, enqueueSnackbar]);

    const { getFormStructure } = useFormStructureGenericModal({
        to,
        cc,
        cci,
        subject,
        body,
        attachments,
        searchParamsTiersIds,
        showTo,
        showCC,
        showCCI,
        showSubject,
        showBody,
        showIaButtons,
        iaChildren: defaultIaButtons,
        showAttachments,
        additionnalReactNode,
        SxProps,
        customButtonsFooter,
        fieldsOrdering,
        submittingForm,
    });
    type MailFormObject = {
        recipients: ContactItem[] | undefined;
        recipientsCC: ContactItem[] | undefined;
        recipientsCCI: ContactItem[] | undefined;
        subject: string | undefined;
        mailBody: string | undefined;
    };

    useEffect(() => {
        senderEmail.current = keycloak.getEmail();
        senderName.current = keycloak.getName();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = useCallback<SubmitHandler<MailFormObject>>(
        async (values) => {
            if (submittingForm) {
                return;
            }
            setSubmittingForm(true);
            let mailRecipientsTo = to.map((recipient) => recipient.emailAddress);
            if (values.recipients && values.recipients.length > 0) {
                mailRecipientsTo = values.recipients.map((recipient) => recipient.emailAddress);
            }

            // Is it a good idea to check recipientsCC length > 0 if trying to remove original pre values ??????
            let mailRecipientsCC = cc;
            if (values.recipientsCC && values.recipientsCC.length > 0) {
                mailRecipientsCC = values.recipientsCC.map((recipient) => recipient.emailAddress);
            }

            let mailRecipientsCCI = cci;
            if (values.recipientsCCI && values.recipientsCCI.length > 0) {
                mailRecipientsCCI = values.recipientsCCI.map((recipient) => recipient.emailAddress);
            }

            let mailSubject = subject;
            if (values.subject) {
                mailSubject = values.subject;
            }

            let mailBody = body;
            if (values.mailBody) {
                mailBody = values.mailBody as string;
            }
            if (autoBalisesForMail && mailBody) {
                mailBody = markDownToHtml({ content: mailBody });
            }

            const request: MailApiSendMailMailCollectionRequest = {
                attachments2: files,
                details: {
                    from: senderEmail.current,
                    fromName: senderName.current,
                    tos: mailRecipientsTo,
                    ccs: mailRecipientsCC || null,
                    bccs: mailRecipientsCCI || null,
                    subject: mailSubject || null,
                    body: mailBody || null,
                    status: 'pending',
                    tags: ['?'],
                    replies: [senderEmail.current],
                    // @ts-expect-error : Let's ignore undefined property
                    useTemplating: true,
                    templateData: {
                        contentTemplate: 'default-content',
                        mailContent: mailBody,
                        logo: {
                            alt: 'Legal2Digital Logo',
                            src: '/shared/images/LogoLegal2DigitalLight.png',
                            title: 'Legal2Digital',
                        },
                    },
                },
            };

            const emailPromise = await mailApi.sendMailMailCollection(request);
            setSubmittingForm(false);
            if (emailPromise.status === 201) {
                enqueueSnackbar('Le mail a bien été envoyé');
                if (onSuccess && typeof onSuccess !== 'undefined') {
                    onSuccess();
                } else {
                    modalActions.close();
                }
            } else {
                enqueueSnackbar("Une erreur est survenue lors de l'enovi du mail", {
                    variant: 'error',
                });
                modalActions.close();
            }
        },
        [
            files,
            body,
            cc,
            cci,
            subject,
            to,
            mailApi,
            enqueueSnackbar,
            modalActions,
            autoBalisesForMail,
            submittingForm,
            onSuccess,
        ],
    );

    /**
     * Prevent form submission when Enter key is pressed
     */
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLFormElement>) => {
        if (e.key === 'Enter' && e.ctrlKey === false) {
            e.preventDefault();
        }
    }, []);

    const formProps = {
        formContext,
        formObject: {},
        onkeydown: handleKeyDown,
        onSubmit: handleSubmit,
        gridProps: { sx: { gap: '8px' } },
        submitButton: {
            label: 'Envoyer',
            displayed: false,
        },
        isSubmitting: submittingForm,
    };

    useEffect(() => {
        if (typeof getFormContext === 'function') {
            getFormContext({
                getMailBody: () => formContext.getValues('mailBody') as string | undefined,
                setMailBody: (value: string) => formContext.setValue('mailBody', value),
            });
        }
    }, [getFormContext, formContext]);

    return (
        <Box sx={{ minWidth: '20rem', maxWidth: '40rem', ...SxProps }}>
            <FlexyForm
                formStructure={getFormStructure()}
                {...formProps}
            />
        </Box>
    );
};

export default GenericModalMailContent;
